import React, { useId } from 'react';
import { Input } from '@/components/ui/input';

export interface NumberRangeValuesSectionProps {
  selectedValues: string[];
  onRangeChange: (minValue: string, maxValue: string) => void;
  validationErrors?: string[];
  /** Known column bounds, shown as placeholder hints only — never clamped. */
  min?: number;
  max?: number;
}

/**
 * NumberRangeValuesSection - Min/Max numeric inputs for range filters.
 * Values are stored as two strings: [min, max]; either bound may be empty.
 * No clamping — typed values (including above the known max) propagate verbatim.
 */
export const NumberRangeValuesSection: React.FC<
  NumberRangeValuesSectionProps
> = ({ selectedValues, onRangeChange, validationErrors = [], min, max }) => {
  const minId = useId();
  const maxId = useId();

  const minValue = selectedValues[0] ?? '';
  const maxValue = selectedValues[1] ?? '';
  const errorMessage = validationErrors.find(Boolean);

  const handleMinChange = (value: string): void => {
    onRangeChange(value, maxValue);
  };

  const handleMaxChange = (value: string): void => {
    onRangeChange(minValue, value);
  };

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex items-start gap-3">
        <div className="flex flex-1 flex-col gap-2">
          <label
            htmlFor={minId}
            className="text-xs font-medium text-muted-foreground"
          >
            Min
          </label>
          <Input
            id={minId}
            type="number"
            value={minValue}
            onChange={event => handleMinChange(event.target.value)}
            placeholder={min !== undefined ? `e.g. ${min}` : undefined}
            className="h-9 placeholder:text-muted-foreground/50"
          />
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <label
            htmlFor={maxId}
            className="text-xs font-medium text-muted-foreground"
          >
            Max
          </label>
          <Input
            id={maxId}
            type="number"
            value={maxValue}
            onChange={event => handleMaxChange(event.target.value)}
            placeholder={max !== undefined ? `e.g. ${max}` : undefined}
            className="h-9 placeholder:text-muted-foreground/50"
          />
        </div>
      </div>
      {errorMessage && (
        <div className="text-xs text-destructive">{errorMessage}</div>
      )}
    </div>
  );
};
